import React, { useRef, useState } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { Badge, FormGroup, Spinner } from 'reactstrap';
import { actualCountry } from '../../selectors/country';
import {
  MAX_GALLERY_IMAGES,
  GALLERY_RATIO_VALUE,
  adaptCropToRatio,
} from '../LandingPageEditor/landingComponents';
import GalleryCropModal from './GalleryCropModal';
import useLandingAssetUpload from './useLandingAssetUpload';

const PATH = 'landingComponents.gallery.images';

const THUMB_WIDTH = 64;


// La miniatura prende la forma del rapporto scelto, non i 64x48 fissi di
// prima: un ritaglio 16:9 dentro un riquadro 4:3 si vedrebbe deformato.
//
// Il colore di sfondo è quello pubblicato (--l1-gal-bg, cioè
// gallery.backgroundColor): con `contain` come default le bande si vedono su
// ogni immagine che non combacia col rapporto, e un colore fisso mostrerebbe
// bande sbagliate ogni volta che l'operatore cambia Background Color.
const thumbBox = (ratioValue, backgroundColor) => ({
  position: 'relative',
  overflow: 'hidden',
  flex: '0 0 auto',
  width: `${THUMB_WIDTH}px`,
  height: `${Math.round(THUMB_WIDTH / ratioValue)}px`,
  borderRadius: '4px',
  background: backgroundColor || '#f7f9fb',
});

// Stessa matematica di .l1c-slide.is-cropped img nel runtime: il pannello deve
// mostrare ciò che verrà pubblicato, non un'approssimazione.
const thumbImage = (crop, imageFit) =>
  crop
    ? {
        position: 'absolute',
        width: `${100 / crop.w}%`,
        height: `${100 / crop.h}%`,
        left: `${(-crop.x * 100) / crop.w}%`,
        top: `${(-crop.y * 100) / crop.h}%`,
        maxWidth: 'none',
        objectFit: 'fill',
      }
    : { width: '100%', height: '100%', objectFit: imageFit };

// flexShrink 0 insieme al minWidth 0 sulla colonna centrale: senza, un badge
// lungo come "Crop needs review" comprime i pulsanti (misurati a 24px invece
// di 31) e li fa scivolare a destra. Tutto il restringimento deve cadere sulla
// colonna del testo, che può andare a capo; i pulsanti restano fermi.
const IconButton = ({ title, icon, color, disabled, onClick }) => (
  <button
    type="button"
    title={title}
    disabled={disabled}
    className={`btn btn-${color} btn-sm p-0 ml-1`}
    style={{ width: '31px', height: '31px', flexShrink: 0 }}
    onClick={onClick}>
    <i className={`bx ${icon} d-block font-size-15`} />
  </button>
);

const GalleryImagesFieldArray = () => {
  const { values } = useFormikContext();
  const gallery = values.landingComponents.gallery;
  const images = gallery.images || [];
  const country = useSelector(actualCountry);
  const countryId = country ? country.id : null;
  const inputRef = useRef(null);
  // L'avviso vive solo per la sessione: è un consiglio al momento del
  // caricamento, non un dato della landing, e non finisce in Firestore.
  const [lowRes, setLowRes] = useState({});
  const [cropIndex, setCropIndex] = useState(null);
  const { uploading, upload } = useLandingAssetUpload(countryId);

  const full = images.length >= MAX_GALLERY_IMAGES;
  const ratioValue = GALLERY_RATIO_VALUE[gallery.aspectRatio] || 16 / 9;

  return (
    <FieldArray
      name={PATH}
      render={(arrayHelpers) => (
        <FormGroup>
          {images.map((image, index) => {
            // Il ritaglio salvato non viene mai riscritto al cambio di
            // rapporto: qui se ne mostra la versione derivata, e il badge
            // segnala che vale la pena tornarci sopra.
            const derived = adaptCropToRatio(image.crop, gallery.aspectRatio);
            const needsReview =
              Boolean(image.crop) && image.crop.ratio !== gallery.aspectRatio;

            return (
              <div
                key={image.url || index}
                className="d-flex align-items-center flex-wrap border rounded p-2 mb-2">
                <div style={thumbBox(ratioValue, gallery.backgroundColor)}>
                  <img
                    src={image.url}
                    alt=""
                    style={thumbImage(derived, gallery.imageFit || 'contain')}
                  />
                </div>
                <div className="flex-grow-1 ml-2" style={{ minWidth: 0 }}>
                  <div className="font-size-11 text-muted">#{index + 1}</div>
                </div>
                <IconButton
                  title="Crop"
                  icon="bx-crop"
                  color="light"
                  onClick={() => setCropIndex(index)}
                />
                <IconButton
                  title="Move up"
                  icon="bx-up-arrow-alt"
                  color="light"
                  disabled={index === 0}
                  onClick={() => arrayHelpers.swap(index, index - 1)}
                />
                <IconButton
                  title="Move down"
                  icon="bx-down-arrow-alt"
                  color="light"
                  disabled={index === images.length - 1}
                  onClick={() => arrayHelpers.swap(index, index + 1)}
                />
                <IconButton
                  title="Remove"
                  icon="bx-trash"
                  color="danger"
                  onClick={() => arrayHelpers.remove(index)}
                />
                {/* I badge stanno su una riga propria a piena larghezza: nella
                    colonna del titolo, larga una settantina di pixel, "Crop
                    needs review" andava a capo tre volte. La riga si aggiunge
                    solo quando c'è almeno un badge, così le righe pulite non
                    cambiano altezza. */}
                {(image.crop || lowRes[image.url]) && (
                  <div className="w-100 mt-2">
                    {image.crop && !needsReview && (
                      <Badge color="info">Cropped</Badge>
                    )}
                    {needsReview && (
                      <Badge color="warning">Crop needs review</Badge>
                    )}
                    {lowRes[image.url] && (
                      <Badge color="warning" className="ml-1">
                        Low resolution
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {cropIndex !== null && images[cropIndex] && (
            <GalleryCropModal
              key={`${cropIndex}-${gallery.aspectRatio}`}
              image={images[cropIndex]}
              aspectRatio={gallery.aspectRatio}
              onClose={() => setCropIndex(null)}
              onSave={(crop) => {
                arrayHelpers.replace(cropIndex, {
                  ...images[cropIndex],
                  crop,
                });
                setCropIndex(null);
              }}
              onRemove={() => {
                arrayHelpers.replace(cropIndex, { url: images[cropIndex].url });
                setCropIndex(null);
              }}
            />
          )}

          <input
            type="file"
            multiple
            accept="image/*"
            ref={inputRef}
            style={{ display: 'none' }}
            onChange={(event) => {
              const files = Array.from(event.target.files || []);
              event.target.value = '';
              const room = MAX_GALLERY_IMAGES - images.length;
              upload(files.slice(0, room), ({ url, lowRes: isLowRes }) => {
                arrayHelpers.push({ url });
                if (isLowRes) setLowRes((current) => ({ ...current, [url]: true }));
              });
            }}
          />
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            disabled={uploading || full}
            onClick={() => inputRef.current && inputRef.current.click()}>
            {uploading ? (
              <Spinner size="sm" className="mr-1" />
            ) : (
              <i className="bx bx-plus align-middle mr-1" />
            )}
            Add images
          </button>
          <p className="text-muted small mb-0 mt-2">
            Up to {MAX_GALLERY_IMAGES} images, 5MB each. Upload them at about
            twice the width they will be shown at, or they will look soft on
            phone screens.
          </p>
        </FormGroup>
      )}
    />
  );
};

export default GalleryImagesFieldArray;
